// src/client/roadLogic.ts
// Pure Big Road placement + derived-road (Big Eye Boy / Small Road / Cockroach Road) logic,
// extracted out of index.tsx so it can be unit-tested without a browser/React runtime
// (see test/derivedRoads.test.ts).

export const roadRows = 6;
export const roadColumns = 30;

export type RoadEntry = { color: string; sourceIndex: number };
export type RoadCell = RoadEntry | null;

export const placeRoad = (entries: RoadEntry[]): RoadCell[][] => {
  const grid = Array.from({ length: roadRows }, () => Array<RoadCell>(roadColumns).fill(null));
  const positions: Array<{ row: number; column: number }> = [];
  let lastColor = '';
  let lastRow = 0;
  let lastColumn = 0;
  let runStartColumn = 0;

  entries.forEach((entry) => {
    let row = 0;
    let column = 0;
    if (!positions.length) {
      row = 0;
      column = 0;
    } else if (entry.color !== lastColor) {
      column = Math.min(runStartColumn + 1, roadColumns - 1);
      while (column < roadColumns - 1 && grid[0][column]) column += 1;
      row = 0;
    } else {
      row = lastRow + 1;
      column = lastColumn;
      if (row >= roadRows || grid[row][column]) {
        row = row >= roadRows ? roadRows - 1 : lastRow;
        column = Math.min(lastColumn + 1, roadColumns - 1);
        while (column < roadColumns - 1 && grid[row][column]) column += 1;
      }
    }
    grid[row][column] = entry;
    positions.push({ row, column });
    lastColor = entry.color;
    lastRow = row;
    lastColumn = column;
    if (positions.length === 1 || entry.color !== entries[entry.sourceIndex - 1]?.color) {
      runStartColumn = column;
    }
  });
  return grid;
};

export const getBigEntries = (outcomes: string[]): RoadEntry[] =>
  outcomes.map((color, sourceIndex) => ({ color, sourceIndex }));

export const getColumns = (entries: RoadEntry[]) => {
  const grid = placeRoad(entries);
  const columns: Array<Array<{ row: number; entry: RoadEntry }>> = Array.from({ length: roadColumns }, () => []);
  grid.forEach((row, rowIndex) => row.forEach((entry, column) => {
    if (entry) columns[column].push({ row: rowIndex, entry });
  }));
  return columns.filter((column) => column.length > 0);
};

// Big Eye Boy / Small Road / Cockroach Road all derive from the Big Road with the same
// look-back rule, offset by k columns (k=1/2/3 for Big Eye/Small/Cockroach). The first bead
// of a new Big Road column compares the *lengths* of the two preceding reference columns
// ("齊腳" same length = red, "突腳" different = blue); a bead continuing an existing column
// compares the column k back at the same row ("拍拍連" filled = red, "一廳兩房" empty then
// filled above = blue, "長龍" empty both = red).
export const getDerivedEntries = (outcomes: string[], offset: number): RoadEntry[] => {
  const k = offset - 1;
  const columns = getColumns(getBigEntries(outcomes));
  const derived: RoadEntry[] = [];
  const hasRow = (columnIndex: number, row: number) =>
    row >= 0 && Boolean(columns[columnIndex]?.some((cell) => cell.row === row));

  columns.forEach((column, columnIndex) => {
    column.forEach(({ row }, indexInColumn) => {
      if (indexInColumn === 0) {
        if (columnIndex < k + 1) return;
        const sameCount = columns[columnIndex - 1].length === columns[columnIndex - 1 - k].length;
        derived.push({ color: sameCount ? 'red' : 'blue', sourceIndex: derived.length });
      } else {
        if (columnIndex < k) return;
        const refColumn = columnIndex - k;
        const color = hasRow(refColumn, row) ? 'red' : (hasRow(refColumn, row - 1) ? 'blue' : 'red');
        derived.push({ color, sourceIndex: derived.length });
      }
    });
  });
  return derived;
};
